import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { TranslationBlueprint } from '@backstage/plugin-app-react';
import { createTranslationMessages } from '@backstage/core-plugin-api/alpha';
import { catalogTranslationRef } from '@backstage/plugin-catalog/alpha';
import { catalogReactTranslationRef } from '@backstage/plugin-catalog-react/alpha';
import { coreComponentsTranslationRef } from '@backstage/core-components/alpha';
import { searchTranslationRef } from '@backstage/plugin-search/alpha';
import { scaffolderTranslationRef } from '@backstage/plugin-scaffolder/alpha';
import { userSettingsTranslationRef } from '@backstage/plugin-user-settings/alpha';
import { apiDocsTranslationRef } from '@backstage/plugin-api-docs/alpha';

/*
 * Traducciones al español. Solo se sobrescriben las claves visibles y se
 * usa `full: false`, así que el resto de textos conserva el inglés original.
 *
 * Nota: TechDocs no expone TranslationRef en 1.50, por lo que no se traduce
 * desde aquí. Sus textos de interfaz siguen en inglés.
 */

const catalogo = TranslationBlueprint.make({
  name: 'catalog',
  params: {
    resource: createTranslationMessages({
      ref: catalogTranslationRef,
      full: false,
      messages: {
        'indexPage.title': '{{orgName}} Catálogo',
        'indexPage.createButtonTitle': 'Crear',
        'indexPage.supportButtonContent':
          'Todos los componentes de software del catálogo',
        'entityPage.notFoundMessage':
          'No existe ningún {{kind}} con el {{link}} solicitado.',
        'entityPage.notFoundLinkText': 'tipo, namespace y nombre',
        'entityNotFound.title': 'No se ha encontrado la entidad',
        'entityNotFound.docButtonTitle': 'DOCUMENTACIÓN',
        'aboutCard.title': 'Acerca de',
        'aboutCard.unknown': 'desconocido',
        'aboutCard.refreshButtonTitle': 'Programar actualización de la entidad',
        'aboutCard.editButtonTitle': 'Editar metadatos',
        'aboutCard.editButtonAriaLabel': 'Editar',
        'aboutCard.createSimilarButtonTitle': 'Crear algo similar',
        'aboutCard.refreshScheduledMessage': 'Actualización programada',
        'aboutCard.refreshButtonAriaLabel': 'Actualizar',
        'aboutCard.launchTemplate': 'Lanzar plantilla',
        'aboutCard.viewTechdocs': 'Ver TechDocs',
        'aboutCard.viewSource': 'Ver código fuente',
        'aboutCard.descriptionField.value': 'Sin descripción',
        'aboutCard.descriptionField.label': 'Descripción',
        'aboutCard.ownerField.value': 'Sin propietario',
        'aboutCard.ownerField.label': 'Propietario',
        'aboutCard.domainField.value': 'Sin dominio',
        'aboutCard.domainField.label': 'Dominio',
        'aboutCard.systemField.value': 'Sin sistema',
        'aboutCard.systemField.label': 'Sistema',
        'aboutCard.parentComponentField.value': 'Sin componente padre',
        'aboutCard.parentComponentField.label': 'Componente padre',
        'aboutCard.kindField.label': 'Tipo de entidad',
        'aboutCard.typeField.label': 'Tipo',
        'aboutCard.lifecycleField.label': 'Ciclo de vida',
        'aboutCard.tagsField.value': 'Sin etiquetas',
        'aboutCard.tagsField.label': 'Etiquetas',
        'aboutCard.targetsField.label': 'Destinos',
        'searchResultItem.type': 'Tipo',
        'searchResultItem.kind': 'Tipo de entidad',
        'searchResultItem.owner': 'Propietario',
        'searchResultItem.lifecycle': 'Ciclo de vida',
        'catalogTable.allFilters': 'Todos',
        'catalogTable.warningPanelTitle':
          'No se pudieron cargar las entidades del catálogo.',
        'catalogTable.viewActionTitle': 'Ver',
        'catalogTable.editActionTitle': 'Editar',
        'catalogTable.starActionTitle': 'Añadir a favoritos',
        'catalogTable.unStarActionTitle': 'Quitar de favoritos',
        'entityLinksCard.title': 'Enlaces',
        'entityLinksCard.readMoreButtonTitle': 'Leer más',
        'entityLabelsCard.title': 'Etiquetas',
        'entityLabelsCard.readMoreButtonTitle': 'Leer más',
        'entityContextMenu.moreButtonTitle': 'Más',
        'entityContextMenu.copiedMessage': '¡Copiado!',
        'entityContextMenu.inspectMenuTitle': 'Inspeccionar entidad',
        'entityContextMenu.copyURLMenuTitle': 'Copiar URL de la entidad',
        'entityContextMenu.unregisterMenuTitle': 'Desregistrar entidad',
        'entityTabs.tabsAriaLabel': 'Pestañas',
        'deleteEntity.description':
          'Esta entidad no está referenciada por ninguna ubicación y, por tanto, no recibe actualizaciones.',
        'deleteEntity.cancelButtonTitle': 'Cancelar',
        'deleteEntity.deleteButtonTitle': 'Eliminar',
        'deleteEntity.dialogTitle':
          '¿Seguro que quieres eliminar esta entidad?',
        'deleteEntity.actionButtonTitle': 'Eliminar entidad',
        'dependsOnComponentsCard.title': 'Depende de componentes',
        'dependsOnComponentsCard.emptyMessage':
          'Ningún componente es dependencia de este componente.',
        'dependencyOfComponentsCard.title': 'Dependencia de componentes',
        'dependencyOfComponentsCard.emptyMessage':
          'Ningún componente depende de este componente.',
        'hasSubcomponentsCard.title': 'Tiene subcomponentes',
        'hasSubcomponentsCard.emptyMessage':
          'Ningún subcomponente forma parte de este componente.',
        'hasSystemsCard.title': 'Tiene sistemas',
        'hasSystemsCard.emptyMessage':
          'Ningún sistema forma parte de este dominio.',
        'systemDiagramCard.title': 'Diagrama del sistema',
        'systemDiagramCard.description':
          'Usa pellizcar y ampliar para moverte por el diagrama.',
        'systemDiagramCard.edgeLabels.partOf': 'forma parte de',
        'systemDiagramCard.edgeLabels.dependsOn': 'depende de',
        'systemDiagramCard.edgeLabels.provides': 'proporciona',
      },
    }),
  },
});

const catalogoReact = TranslationBlueprint.make({
  name: 'catalog-react',
  params: {
    resource: createTranslationMessages({
      ref: catalogReactTranslationRef,
      full: false,
      messages: {
        'catalogFilter.title': 'Filtros',
        'catalogFilter.buttonTitle': 'Filtros',
        'entitySearchBar.placeholder': 'Buscar',
        'entityKindPicker.title': 'Tipo de entidad',
        'entityKindPicker.errorMessage':
          'No se pudieron cargar los tipos de entidad',
        'entityTypePicker.title': 'Tipo',
        'entityTypePicker.errorMessage': 'No se pudieron cargar los tipos',
        'entityTypePicker.optionAllTitle': 'todos',
        'entityLifecyclePicker.title': 'Ciclo de vida',
        'entityNamespacePicker.title': 'Espacio de nombres',
        'entityOwnerPicker.title': 'Propietario',
        'entityProcessingStatusPicker.title': 'Estado de procesamiento',
        'entityTagPicker.title': 'Etiquetas',
        'userListPicker.defaultOrgName': 'Empresa',
        'userListPicker.orgFilterAllLabel': 'Todos',
        'userListPicker.personalFilter.title': 'Personal',
        'userListPicker.personalFilter.ownedLabel': 'Propios',
        'userListPicker.personalFilter.starredLabel': 'Favoritos',
        'favoriteEntity.addToFavorites': 'Añadir a favoritos',
        'favoriteEntity.removeFromFavorites': 'Quitar de favoritos',
        'entityTableColumnTitle.name': 'Nombre',
        'entityTableColumnTitle.type': 'Tipo',
        'entityTableColumnTitle.label': 'Etiqueta',
        'entityTableColumnTitle.title': 'Título',
        'entityTableColumnTitle.description': 'Descripción',
        'entityTableColumnTitle.domain': 'Dominio',
        'entityTableColumnTitle.system': 'Sistema',
        'entityTableColumnTitle.namespace': 'Espacio de nombres',
        'entityTableColumnTitle.tags': 'Etiquetas',
        'entityTableColumnTitle.lifecycle': 'Ciclo de vida',
        'entityTableColumnTitle.owner': 'Propietario',
        'entityTableColumnTitle.targets': 'Destinos',
        'inspectEntityDialog.title': 'Inspector de entidades',
        'inspectEntityDialog.closeButtonTitle': 'Cerrar',
        'inspectEntityDialog.overviewPage.title': 'Resumen',
        'inspectEntityDialog.overviewPage.metadata.title': 'Metadatos',
        'inspectEntityDialog.overviewPage.labels': 'Etiquetas',
        'inspectEntityDialog.overviewPage.status.title': 'Estado',
        'inspectEntityDialog.overviewPage.identity.title': 'Identidad',
        'inspectEntityDialog.overviewPage.annotations': 'Anotaciones',
        'inspectEntityDialog.overviewPage.tags': 'Etiquetas',
        'inspectEntityDialog.overviewPage.relation.title': 'Relaciones',
        'inspectEntityDialog.tabNames.json': 'JSON sin procesar',
        'inspectEntityDialog.tabNames.yaml': 'YAML sin procesar',
        'inspectEntityDialog.tabNames.overview': 'Resumen',
        'inspectEntityDialog.tabNames.ancestry': 'Ascendencia',
        'inspectEntityDialog.tabNames.colocated': 'Colocados',
        'missingAnnotationEmptyState.title': 'Falta una anotación',
        'missingAnnotationEmptyState.readMore': 'Leer más',
      },
    }),
  },
});

const componentesCore = TranslationBlueprint.make({
  name: 'core-components',
  params: {
    resource: createTranslationMessages({
      ref: coreComponentsTranslationRef,
      full: false,
      messages: {
        'table.filter.title': 'Filtros',
        'table.filter.placeholder': 'Todos los resultados',
        'table.filter.clearAll': 'Limpiar todo',
        'table.body.emptyDataSourceMessage': 'No hay registros que mostrar',
        'table.header.actions': 'Acciones',
        'table.toolbar.search': 'Filtrar',
        'table.pagination.labelDisplayedRows': '{from}-{to} de {count}',
        'table.pagination.labelRowsSelect': 'filas',
        'table.pagination.firstTooltip': 'Primera página',
        'table.pagination.lastTooltip': 'Última página',
        'table.pagination.nextTooltip': 'Página siguiente',
        'table.pagination.previousTooltip': 'Página anterior',
        'emptyState.missingAnnotation.title': 'Falta una anotación',
        'emptyState.missingAnnotation.readMore': 'Leer más',
        'signIn.title': 'Iniciar sesión',
        'signIn.loginFailed': 'Error al iniciar sesión',
        'signIn.guestProvider.title': 'Invitado',
        'signIn.guestProvider.enter': 'Entrar',
        'oauthRequestDialog.title': 'Inicio de sesión requerido',
        'oauthRequestDialog.login': 'Iniciar sesión',
        'oauthRequestDialog.rejectAll': 'Rechazar todo',
        'simpleStepper.next': 'Siguiente',
        'simpleStepper.back': 'Atrás',
        'simpleStepper.finish': 'Finalizar',
        'simpleStepper.reset': 'Reiniciar',
        'simpleStepper.skip': 'Omitir',
        'errorPage.title': 'Parece que algo ha salido mal',
        'errorPage.subtitle': 'ERROR {{status}}: {{statusMessage}}',
        'errorPage.goBack': 'Volver',
        'errorPage.showMoreDetails': 'Mostrar más detalles',
        'errorPage.showLessDetails': 'Mostrar menos detalles',
        'supportButton.title': 'Soporte',
        'supportButton.close': 'Cerrar',
        'copyTextButton.tooltipText': 'Texto copiado al portapapeles',
        'proxiedSignInPage.title':
          'No parece que hayas iniciado sesión. Vuelve a cargar la página del navegador.',
        'autoLogout.stillTherePrompt.title': 'Cerrando sesión por inactividad',
        'autoLogout.stillTherePrompt.buttonText': '¡Sí! No cerrar mi sesión',
        'logViewer.searchField.placeholder': 'Buscar',
        'logViewer.downloadBtn.tooltip': 'Descargar registros',
        'dependencyGraph.fullscreenTooltip': 'Alternar pantalla completa',
      },
    }),
  },
});

const buscador = TranslationBlueprint.make({
  name: 'search',
  params: {
    resource: createTranslationMessages({
      ref: searchTranslationRef,
      full: false,
      messages: {
        'searchModal.viewFullResults': 'Ver todos los resultados',
        'searchType.tabs.allTitle': 'Todos',
        'searchType.allResults': 'Todos los resultados',
        'searchType.accordion.collapse': 'Contraer',
        'searchType.accordion.numberOfResults': '{{number}} resultados',
        'searchType.accordion.allTitle': 'Todos',
        'sidebarSearchModal.title': 'Buscar',
      },
    }),
  },
});

const scaffolder = TranslationBlueprint.make({
  name: 'scaffolder',
  params: {
    resource: createTranslationMessages({
      ref: scaffolderTranslationRef,
      full: false,
      messages: {
        'templateListPage.title': 'Crear un nuevo componente',
        'templateListPage.subtitle':
          'Crea nuevos componentes de software con plantillas estándar de tu organización',
        'templateListPage.pageTitle': 'Crear un nuevo componente',
        'templateListPage.templateGroups.defaultTitle': 'Plantillas',
        'templateListPage.templateGroups.otherTitle': 'Otras plantillas',
        'templateListPage.contentHeader.registerExistingButtonTitle':
          'Registrar componente existente',
        'templateListPage.additionalLinksForEntity.viewTechDocsTitle':
          'Ver TechDocs',
        'templateWizardPage.templateWithTitle': 'Crear nueva {{templateTitle}}',
        'templateWizardPage.pageContextMenu.editConfigurationTitle':
          'Editar configuración',
        'templateTypePicker.title': 'Categorías',
        'listTaskPage.title': 'Listar tareas de plantillas',
        'listTaskPage.pageTitle': 'Tareas de plantillas',
        'listTaskPage.subtitle': 'Todas las tareas que se han iniciado',
        'listTaskPage.content.tableTitle': 'Tareas',
        'listTaskPage.content.emptyState.title':
          'No hay información que mostrar',
        'listTaskPage.content.tableCell.template': 'Plantilla',
        'listTaskPage.content.tableCell.status': 'Estado',
        'listTaskPage.content.tableCell.owner': 'Propietario',
        'listTaskPage.content.tableCell.created': 'Creada',
        'listTaskPage.content.tableCell.taskID': 'ID de tarea',
        'ownerListPicker.title': 'Propietario de la tarea',
        'ownerListPicker.options.all': 'Todas',
        'ownerListPicker.options.owned': 'Propias',
        'ongoingTask.title': 'Ejecución de',
        'ongoingTask.subtitle': 'Tarea {{taskId}}',
        'ongoingTask.pageTitle.hasTemplateName':
          'Ejecución de {{templateName}}',
        'ongoingTask.pageTitle.noTemplateName': 'Ejecución de Scaffolder',
        'ongoingTask.cancelButtonTitle': 'Cancelar',
        'ongoingTask.retryButtonTitle': 'Reintentar',
        'ongoingTask.startOverButtonTitle': 'Empezar de nuevo',
        'ongoingTask.hideLogsButtonTitle': 'Ocultar registros',
        'ongoingTask.showLogsButtonTitle': 'Mostrar registros',
        'ongoingTask.contextMenu.cancel': 'Cancelar',
        'ongoingTask.contextMenu.retry': 'Reintentar',
        'ongoingTask.contextMenu.startOver': 'Empezar de nuevo',
        'ongoingTask.contextMenu.moreOptions': 'Más opciones',
        'ongoingTask.contextMenu.hideLogs': 'Ocultar registros',
        'ongoingTask.contextMenu.showLogs': 'Mostrar registros',
        'fields.entityNamePicker.title': 'Nombre',
        'fields.entityNamePicker.description': 'Nombre único del componente',
        'fields.ownerPicker.title': 'Propietario',
        'fields.ownerPicker.description': 'El propietario del componente',
        'fields.repoOwnerPicker.title': 'Propietario',
        'fields.repoOwnerPicker.description': 'El propietario del repositorio',
        'fields.repoUrlPicker.host.title': 'Host',
        'fields.repoUrlPicker.host.description':
          'El host donde se creará el repositorio',
        'fields.repoUrlPicker.repository.title': 'Repositorios disponibles',
        'fields.repoUrlPicker.repository.description':
          'El nombre del repositorio',
        'aboutCard.launchTemplate': 'Lanzar plantilla',
      },
    }),
  },
});

const ajustesUsuario = TranslationBlueprint.make({
  name: 'user-settings',
  params: {
    resource: createTranslationMessages({
      ref: userSettingsTranslationRef,
      full: false,
      messages: {
        'settingsLayout.title': 'Ajustes',
        'defaultSettingsPage.tabsTitle.general': 'General',
        'defaultSettingsPage.tabsTitle.authProviders':
          'Proveedores de autenticación',
        'defaultSettingsPage.tabsTitle.featureFlags': 'Funcionalidades',
        'profileCard.title': 'Perfil',
        'appearanceCard.title': 'Apariencia',
        'identityCard.title': 'Identidad',
        'authProviders.title': 'Proveedores disponibles',
        'languageToggle.title': 'Idioma',
        'languageToggle.description': 'Cambiar el idioma',
        'themeToggle.title': 'Tema',
        'themeToggle.description': 'Cambiar el modo del tema',
        'themeToggle.names.auto': 'Automático',
        'themeToggle.names.dark': 'Oscuro',
        'themeToggle.names.light': 'Claro',
        'signOutMenu.title': 'Cerrar sesión',
        'pinToggle.title': 'Fijar barra lateral',
        'pinToggle.description': 'Evita que la barra lateral se contraiga',
        'featureFlags.title': 'Funcionalidades',
        'featureFlags.filterTitle': 'Filtrar',
        'featureFlags.clearFilter': 'Limpiar filtro',
      },
    }),
  },
});

const apiDocs = TranslationBlueprint.make({
  name: 'api-docs',
  params: {
    resource: createTranslationMessages({
      ref: apiDocsTranslationRef,
      full: false,
      messages: {
        'defaultApiExplorerPage.title': 'APIs',
        'defaultApiExplorerPage.subtitle': 'Explorador de APIs de {{orgName}}',
        'defaultApiExplorerPage.pageTitleOverride': 'APIs',
        'defaultApiExplorerPage.createButtonTitle': 'Registrar API existente',
        'defaultApiExplorerPage.supportButtonTitle': 'Todas tus APIs',
        'consumedApisCard.title': 'APIs consumidas',
        'hasApisCard.title': 'APIs',
        'providedApisCard.title': 'APIs proporcionadas',
        'consumingComponentsCard.title': 'Consumidores',
        'providingComponentsCard.title': 'Proveedores',
        'consumingComponentsCard.emptyContent.title':
          'Ningún componente consume esta API.',
        'providingComponentsCard.emptyContent.title':
          'Ningún componente proporciona esta API.',
        'apiDefinitionDialog.closeButtonTitle': 'Cerrar',
        'apiDefinitionCard.rawButtonTitle': 'Sin procesar',
      },
    }),
  },
});

export const i18nModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    catalogo,
    catalogoReact,
    componentesCore,
    buscador,
    scaffolder,
    ajustesUsuario,
    apiDocs,
  ],
});
